import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectShops, selectAppointments } from "./redux/selectors";
import ReactGA4 from "react-ga4";
import TagManager from "react-gtm-module";
import { formatPhoneNumber } from "utils";
import "./css/index.css";

import { popupContainer } from "css";
import { InitOptions } from "react-ga4/types/ga4";
import { AppointmentState } from "types";
import {
  AppointmentConfirmed,
  AppointmentPicker,
  AppointmentPickerV2,
  CustomerInput,
  ServiceOptions,
  ShopLocations,
  SkipToForm,
  StayOrGo,
  VehicleSelection,
} from "pages";
import Header from "Header";

// Define interfaces for the component props and data structures
interface PopupWidgetProps {
  loading?: boolean;
}

interface ShopLocation {
  // Define shop location properties
  id: string;
  name: string;
  // Add other properties as needed
}

const PopupWidget: React.FC<PopupWidgetProps> = ({ loading = false }) => {
  const [ga4Loaded, setGa4Loaded] = useState<boolean>(false);
  const [gtmLoaded, setGtmLoaded] = useState<boolean>(false);
  const shops = useSelector(selectShops) as ShopLocation[];

  const appointment = useSelector(selectAppointments) as AppointmentState;
  const shopConfig = appointment?.shopData;

  const initializeGA4 = (appointment: AppointmentState): void => {
    const gTags = [
      { trackingId: process.env.REACT_APP_GA4_MEASUREMENT_ID },
    ] as InitOptions[];

    if (
      appointment?.shopData?.pixels?.googleAnalytics?.gTag !== undefined &&
      !ga4Loaded
    ) {
      gTags.push({
        trackingId: appointment.shopData.pixels.googleAnalytics.gTag,
      });

      ReactGA4.initialize(gTags);
      setGa4Loaded(true);
    }
  };

  const initializeGTM = (appointment: AppointmentState): void => {
    if (appointment?.shopData?.pixels?.googleTagManager?.GTMTag && !gtmLoaded) {
      const dcTagManagerArgs = {
        gtmId: appointment.shopData.pixels.googleTagManager.GTMTag,
      };
      TagManager.initialize(dcTagManagerArgs);
      setGtmLoaded(true);
    }
  };

  useEffect(() => {
    initializeGA4(appointment);
    initializeGTM(appointment);
  }, [
    appointment?.shopData?.pixels?.googleAnalytics?.gTag,
    appointment?.shopData?.pixels?.googleTagManager?.GTMTag,
  ]);

  // Render the appropriate component based on the current stage
  const renderStageContent = (): JSX.Element | null => {
    if (!appointment) return null;

    switch (appointment.stage) {
      case 0:
        return <ShopLocations data={shops} appointment={appointment} />;
      case 1:
        return <ServiceOptions appointment={appointment} />;
      case 2:
        return renderAppointmentPicker();
      case 3:
        return <CustomerInput appointment={appointment} />;
      case 4:
        return <VehicleSelection appointment={appointment} />;
      case 5:
        return <AppointmentConfirmed appointment={appointment} />;
      case 6:
        return <StayOrGo appointment={appointment} />;
      case 7:
        return <SkipToForm appointment={appointment} />;
      default:
        return null;
    }
  };

  // Helper method to determine which appointment picker to show
  const renderAppointmentPicker = (): JSX.Element => {
    if (appointment.shopData?.configVersion === "v2") {
      return <AppointmentPickerV2 appointment={appointment} />;
    }

    // Default to V1
    return <AppointmentPicker appointment={appointment} />;
  };

  // Loading state component
  const renderLoading = (): JSX.Element => (
    <div
      style={{
        height: "416px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div className="flex justify-center items-center py-12">
        <center>
          <img
            src="https://bdm-files.s3.amazonaws.com/images/spinners/Gear@1x-0.3s-200px-200px.gif"
            alt="loading"
          />
          <br />
          <br />
          Fetching shop locations...
        </center>
      </div>
    </div>
  );

  const formatPhoneNumber = (phoneNumber: string): string => {
    // Remove all non-digits
    const cleaned = phoneNumber.replace(/\D/g, "");

    // Check if it's a US number with country code
    if (cleaned.length === 11 && cleaned.charAt(0) === "1") {
      // Format as +1(XXX)-XXX-XXXX
      return `+1(${cleaned.substring(1, 4)})-${cleaned.substring(
        4,
        7
      )}-${cleaned.substring(7, 11)}`;
    }
    // Standard 10-digit US number
    else if (cleaned.length === 10) {
      // Format as (XXX)-XXX-XXXX
      return `(${cleaned.substring(0, 3)})-${cleaned.substring(
        3,
        6
      )}-${cleaned.substring(6, 10)}`;
    }

    // If it doesn't match expected formats, return the original
    return phoneNumber;
  };

  return (
    <>
      {/* Full-page overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-70"
        style={{
          zIndex: "999 !important",
          background:
            "linear-gradient(0deg, rgba(255, 255, 255, 0.8) 16%, rgba(212,213,212,0.8) 74%, rgba(194,194,194,0.8) 100%)",
        }}
      ></div>

      {/* Popup container */}
      <div
        className="fixed inset-0 flex items-center justify-center w-full"
        style={{ zIndex: 1000 }}
      >
        <div
          className={`${popupContainer} shadow-lg rounded-[15px] overflow-y-scroll w-full h-full max-h-full md:h-auto md:max-h-[calc(100vh-100px)] md:w-[800px]`}
        >
          <div className="flex flex-col justify-between h-full">
            <div className="top-0 sticky z-10 bg-white shadow-lg">
              <Header appointment={appointment} />
            </div>

            <div
              className="flex-grow"
              style={{
                background:
                  "linear-gradient(0deg, rgba(255,255,255,1) 16%, rgba(212,213,212,1) 74%, rgba(194,194,194,1) 100%)",
              }}
            >
              {shopConfig && (shopConfig as any).enabled === false ? (
                <div className="flex items-center justify-center w-full h-full">
                  <div className="p-20 max-w-md w-full text-center m-auto">
                    <h2 className="text-xl font-semibold mb-4">
                      Online appointments are not currently available.
                    </h2>
                    {shopConfig.shopContact?.phone?.number && (
                      <p className="mb-2">
                        Please call{" "}
                        <a
                          href={`tel:${shopConfig.shopContact.phone.number}`}
                          className="text-blue-600 hover:underline"
                        >
                          {formatPhoneNumber(shopConfig.shopContact.phone.number)}
                        </a>{" "}
                        to book an appointment.
                      </p>
                    )}
                  </div>
                </div>
              ) : loading ? (
                renderLoading()
              ) : (
                <div>{renderStageContent()}</div>
              )}
            </div>
            <div>
            <div className="bg-gray-100 z-10 mt-6 py-3 px-3 sm:px-5 col-span-2 flex justify-between items-center text-[14px] !font-roboto !font-normal">
              {/* Left side - Powered by OktoRocket */}
              <div className="flex items-center">
                <span className="mr-1">Powered by OktoRocket</span>
                <span className="flex items-center">
                  <img
                    src="https://bookings.d14e.io/images/powered-by-logo.png"
                    alt="powered by OktoRocket"
                    className="h-4 sm:h-[18px] w-auto align-middle"
                    style={{ position: "relative", top: "-1px" }}
                  />
                </span>
              </div>

              {/* Right side - Need help section (conditionally rendered) */}
              {(appointment?.stage ?? -1) > 0 &&
                shopConfig?.shopContact?.phone?.number && (
                  <div className="flex items-center">
                    <span className="mr-1">Need help?</span>
                    <a
                      href={`tel:${shopConfig.shopContact.phone.number.replace(
                        /\D/g,
                        ""
                      )}`}
                      className="text-blue-600 hover:underline"
                    >
                      Give us a call
                      <span className="hidden md:inline">
                        {" "}
                        at{" "}
                        {formatPhoneNumber(
                          shopConfig.shopContact.phone.number
                        )}
                      </span>
                    </a>
                  </div>
                )}
            </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PopupWidget;
